import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import isNil from 'utils/isNil';
import { Helmet } from 'react-helmet';
import SEO, { createSEO } from 'components/Core/SEO';
import { testFeatureWheel } from 'utils/intellimize';
import useGlobalHeader from 'hooks/useGlobalHeader';
import useGlobalFooter from 'hooks/useGlobalFooter';
import WebsiteLayout from 'layouts/websiteLayout';
import HomeSystemSection from 'components/Page/Homepage/HomeSystemSection';
import FeatureWheelSection from 'components/Page/Homepage/FeatureWheelSection';
import { oldSystemFeatures, newSystemFeatures } from 'components/Page/Homepage/static/data';
import ContentfulDynamicHero from 'components/Contentful/ContentfulDynamicHero';
import ContentfulRowContent from 'components/Contentful/ContentfulRowContent';
import CallToActionCards from 'components/Sections/CallToActionCards';
import ContentfulCallToAction from 'components/Contentful/ContentfulCallToAction';
import InPageFeaturesV4 from 'components/Contentful/ContentfulInPageFeatures/InPageFeaturesV4';
import ToolboxSection from 'components/Page/Toolbox/ToolboxSection';
import ContentfulTrustbar from 'components/Contentful/ContentfulTrustbar';

const Homepage = props => {
  const { contentfulProspectHome, intellimizeForm, contentfulCallToAction } = props.data;
  const ctaProps = contentfulCallToAction;
  const header = useGlobalHeader();
  const footer = useGlobalFooter();

  const {
    hero,
    trustBarSection,
    callToActionCards,
    allInOneSystem,
    inPageFeatures,
    demoForm,
  } = contentfulProspectHome;

  const seo = createSEO(
    {
      ...contentfulProspectHome.seo,
      pageCategory: 'Home',
      slug: '/',
    },
    props
  );

  const getDemoCta = <ContentfulCallToAction {...ctaProps} />;

  useEffect(() => {
    testFeatureWheel();
  }, []);

  return (
    <WebsiteLayout header={header} footer={footer} hero={hero} stickyHeader>
      <SEO {...seo} />
      <Helmet>
        <meta name="facebook-domain-verification" content="03oo6y055o4omzbapnvlufbcgvttco" />
      </Helmet>
      {hero && <ContentfulDynamicHero {...hero} intellimizeForm={intellimizeForm} />}
      <ContentfulTrustbar {...trustBarSection} />
      <HomeSystemSection
        title={allInOneSystem.title}
        description={
          !isNil(allInOneSystem.description) ? allInOneSystem.description.description : ''
        }
        features={oldSystemFeatures}
        className="home--feature-section-old"
        cta={getDemoCta}
      />

      <InPageFeaturesV4 {...inPageFeatures} />

      <FeatureWheelSection
        title="Grow your business with these powerful features."
        features={newSystemFeatures}
        className="home--feature-section-new"
        style={{ display: 'none' }}
      />

      {callToActionCards.map(item => {
        return (
          <React.Fragment key={item.id}>
            <CallToActionCards
              title={item.title}
              description={item.description}
              backgroundColor={item.bgColor}
              cardType={item.cardType}
              cardInfo={item.cards}
              subTitle={item.subTitle}
              subDescription={item.subDescription}
              subLogos={item.logos}
              cta={ctaProps}
            />
            {item.title === 'Why people love ServiceTitan.' && <ToolboxSection />}
          </React.Fragment>
        );
      })}

      <ContentfulRowContent {...demoForm} />
    </WebsiteLayout>
  );
};

export default Homepage;

export const HomepageQuery = graphql`
  query HomepageQuery {
    contentfulProspectHome(internalName: { eq: "Home Page" }) {
      id
      seo {
        ...Seo
      }
      hero {
        ...DynamicHero
      }
      trustBarSection {
        ...TrustBar
      }
      allInOneSystem {
        backgroundColor
        reversed
        title
        description {
          description
        }
        oneSideImg {
          title
          description
          fluid(quality: 100) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
      inPageFeatures {
        ...InPageFeatures
      }
      callToActionCards {
        ...CardsBlock
      }
      demoForm {
        ...RowContent
      }
    }
    intellimizeForm: contentfulInlineMarketingForm(
      contentful_id: { eq: "1Cz3o5kuY2DMG8agqkyjMj" }
    ) {
      ...InlineMarketingForm
    }
    contentfulCallToAction(internalName: { eq: "Homepage - Demo CTA" }) {
      ...CallToAction
    }
  }
`;
